import type { Translations } from '../types';

export const en: Translations = {
    identityVerification: {
        title: 'Verify your identity',
        mockNotice:
            'Test mode. Evrotrust is not connected. No identity documents or biometrics are collected.',
        consent: 'I understand that this is a test and agree to save a mock verification result.',
        continue: 'Continue verification',
        help: 'If you need help, contact your company representative.',
        expires: 'Invitation valid until {date}',
        invalidTitle: 'This invitation is unavailable',
        invalidDescription:
            'The link has expired, was revoked or is no longer valid. Contact your company representative.',
        error: 'Unable to complete the request. Try again.',
        retry: 'Try again',
        loading: 'Loading invitation…',
        successTitle: 'Verification completed',
        successDescription: 'Your test verification was saved. You can close this page.',
        roles: {
            admin: 'Director',
            beneficiary: 'Beneficiary',
            beneficiary_admin: 'Director and beneficiary',
        },
    },
    kyc: {
        title: 'Document verification',
        openSection: 'Open document verification',
        progress: 'Verification status',
        progressCount: '{completed} of {total} required stages completed',
        refresh: 'Refresh',
        hint: 'Upload the missing documents. Statuses update as verification proceeds.',
        loading: 'Loading verification…',
        retry: 'Try again',
        empty: 'No stages are currently available. Try again later or contact support.',
        documents: 'Company documents',
        identity: 'Identity verification',
        otherChecks: 'Additional checks',
        rejectedHint: 'Verification was unsuccessful. Contact support if needed.',
        openDocument: 'Open document',
        upload: 'Upload file',
        uploadAgain: 'Upload again',
        uploading: 'Uploading…',
        uploadSaved: 'The document has been saved and submitted for review.',
        continue: 'Continue',
        successTitle: 'Company verification complete',
        successDescription: 'All required verification stages have been completed.',
        done: 'Done',
        statuses: {
            completed: 'Verified',
            failed: 'Verification failed',
            in_progress_auto: 'Automatic verification',
            in_progress_manual: 'Under review',
            upload: 'Upload required',
            waiting: 'Awaiting verification',
            notRequired: 'Not required',
        },
        errors: {
            emptyFile: 'The selected file is empty.',
            largeFile: 'The file must not exceed 10 MiB.',
            invalidFile: 'Choose a PDF or JPEG, PNG, GIF, BMP, WebP image.',
            conflict: 'The stage has changed. Data is refreshing; check the available actions.',
            upload: 'The upload could not be confirmed. Check the refreshed list before uploading again.',
            load: 'Could not refresh verification data. Please try again.',
        },
        stages: {
            kbo: {
                title: 'KBO extract',
                description: 'Company details from the official register.',
            },
            company_charter: {
                title: 'Company articles',
                description: 'Current articles of association, including amendments.',
            },
            ubo: {
                title: 'UBO register',
                description: 'Information about the company’s beneficial owners.',
            },
            annual_report: {
                title: 'Annual report',
                description: 'Financial statements for the last completed year.',
            },
            admin_id: {
                title: 'Directors’ identities',
                description: 'Overall verification status of the company’s current directors.',
            },
            beneficiaries: {
                title: 'Beneficial owners’ identities',
                description: 'Overall verification status of the beneficial owners.',
            },
            pep_individual: {
                title: 'Additional director checks',
                description: 'The service team carries out this check.',
            },
            pep_entity: {
                title: 'Additional company checks',
                description: 'The service team carries out this check.',
            },
            risk_report: {
                title: 'Final review',
                description: 'The service team carries out this check.',
            },
            bank_account: {
                title: 'Bank details',
                description: 'Overall verification status of the bank details.',
            },
            other: {
                title: 'Additional check',
                description: 'Company verification status.',
            },
        },
    },
    common: {
        close: 'Close',
        ok: 'OK',
    },
    sidebar: {
        appTitle: 'Office Hof',
        menu: {
            mail: 'Mail',
            office: 'Office',
            payment: 'Payment',
            support: 'Support',
            profile: 'Profile',
            logout: 'Logout',
        },
    },
    login: {
        title: 'Sign in to your account',
        subtitle: 'Personal Account',
        loadingData: 'Loading your data',
        form: {
            emailLabel: 'Login',
            emailPlaceholder: 'Email address',
            passwordLabel: 'Password',
            passwordPlaceholder: 'Enter password',
            rememberMe: 'Remember me',
            rememberMeTooltip: 'Save login data for 3 months',
            submitButton: 'Sign In',
            submitButtonLoading: 'Signing in...',
            forgotPassword: 'Forgot password?',
            noAccountText: "Don't have an account?",
            registerLink: 'Sign up',
            forgotPasswordEmailSubject: 'Please change my password',
            forgotPasswordEmailBody:
                'Please change the password for my account:\nemail: {email},\ncompany: {company}.',
            forgotPasswordEmailRequiredTitle: 'Enter email',
            forgotPasswordEmailRequiredMessage:
                'Please enter your account email in the field above first, then click «Forgot password?».',
            selectCompany: 'Company',
            passwordDisabledHint: 'Enter valid email',
            recoveryTitle: 'Recovery',
            loginLabel: 'Login',
            companyLabel: 'Company',
            newPasswordLabel: 'Your new password',
            copyButton: 'Copy',
            savePasswordWarning: 'Remember the new password!',
            recoveryFailedMessage: 'Failed to recover access, please try again',
            backToLogin: 'Back to login',
            confirmRecoveryMessage: 'Confirm account recovery',
            confirmRecoveryButton: 'Confirm',
        },
        validation: {
            emailRequired: 'Enter email address',
            emailInvalid: 'Enter valid email address',
            passwordRequired: 'Enter password',
            incorrectCredentials: 'Incorrect login or password',
            userNotFound: 'User not found',
        },
        errors: {
            authError: 'Authentication error',
            invalidCredentials: 'Invalid login or password',
            tooManyAttempts: 'Too many attempts, please try again later',
            logoutError: 'Failed to log out',
        },
    },
    registration: {
        title: 'Registration',
        emailLabel: 'Email',
        emailPlaceholder: 'Enter your email',
        continueButton: 'Continue',
        haveAccount: 'Already have an account?',
        signInLink: 'Sign in',
        confirmTitle: 'Confirm email address',
        confirmMessage: 'Enter the code we sent to {email}',
        confirmBack: 'Back',
        changeEmail: 'Use a different email',
        confirmSpamHint: 'No code? Check your Spam folder.',
        confirmRequestAgainIn: 'You can request a new code in: {time}',
        confirmRequestAgainButton: 'Request again',
        validation: {
            emailRequired: 'Enter email',
            emailInvalid: 'Enter a valid email',
        },
        errors: {
            invalidEmail: 'Invalid email',
            rateLimit: 'Too many attempts. Please try again later.',
            serverError: 'Server error, please try again later.',
            wrongCode: 'Wrong code',
        },
        form: {
            stepSelectTariff: 'Choose a plan',
            stepCompanyDetails: 'Fill in your company details',
            stepAddressDetails: 'Fill in your mailing address',
            addressForwardingHint: 'Paper letters and parcels will be forwarded to this address',
            stepPersonalDetails: 'Fill in your personal details',
            stepComplete: 'Done!',
            next: 'Next',
            back: 'Back',
            finish: 'Done',
            stepSelectTariffStub: 'The plan selection form will appear here.',
            stepCompanyDetailsStub: 'Company details fields will appear here.',
            stepPersonalDetailsStub: 'Personal details fields will appear here.',
            stepCompleteStub: 'Registration is complete. Tap Done to go to sign in.',
            loadingRegCustomerData: 'Loading data',
            selectOfficeLabel: 'Select office',
            selectTariffLabel: 'Select plan',
            officeBrussels: 'Brussels',
            officeFlanders: 'Flanders',
            companyNameLabel: 'Company name',
            companyNamePlaceholder: 'Enter company name',
            activityLabel: 'Activity',
            activityPlaceholder: 'Describe company activity',
            innLabel: 'VAT number',
            innPlaceholder: 'Enter VAT number',
            promoCodeLabel: 'Promo code',
            promoCodePlaceholder: 'Enter promo code',
            ifAvailable: 'if available',
            tariffDetailsLink: 'More about tariffs',
            registrationFor: 'Registration for',
            firstNameLabel: 'First name',
            firstNamePlaceholder: 'Enter first name',
            lastNameLabel: 'Last name',
            lastNamePlaceholder: 'Enter last name',
            countryLabel: 'Country',
            countryPlaceholder: 'Select country',
            cityLabel: 'City',
            cityPlaceholder: 'Enter city',
            lineLabel: 'Street and house',
            linePlaceholder: 'Enter street and house',
            postcodeLabel: 'Postcode',
            postcodePlaceholder: 'Enter postcode',
            countryBelgium: 'Belgium',
            countryFrance: 'France',
            countryGermany: 'Germany',
            countryNetherlands: 'Netherlands',
            countryPoland: 'Poland',
            genderLabel: 'Gender',
            genderMale: 'Male',
            genderFemale: 'Female',
            personalAddressLabel: 'Address',
            personalAddressPlaceholder: 'Enter address',
            phoneLabel: 'Phone',
            phonePlaceholder: 'Enter phone',
            passportFileLabel: 'Passport scan',
            uploadPassportFrontButton: 'Upload front side',
            uploadPassportReverseButton: 'Upload reverse side',
            uploadAnotherPassportFileButton: 'Choose another file',
            passportReverseFileLabel: 'Passport reverse side',
            passportFrontSideLabel: 'Front of passport',
            openUploadedFile: 'Open file',
            completeRegistrationTitle: 'Confirm registration',
            completeRegistrationMessage:
                'After confirmation, the completed form will be sent for moderation.',
            completeRegistrationConfirmButton: 'Confirm',
            completeRegistrationCancelButton: 'Cancel',
            completeValidationFieldsTitle: 'Please check the following fields:',
            completeValidationFieldError: 'Please check this field',
            completeValidationInnHint: 'Check the VAT/INN field on the company details step',
            moderationInProgressTitle: 'Your data is under review',
            moderationInProgressDescription:
                'The review takes up to 24 hours. After completion, we will send an email to {email} with your login and password for the personal account.',
            moderationInProgressContact: 'If you have any questions, contact us at:',
            returnToWebsiteButton: 'Back to website',
            registerButton: 'Register',
            requiredField: 'Required field',
            minLengthTemplate: 'Minimum {n} characters',
            maxLengthTemplate: 'Maximum {n} characters',
            phoneInvalid: 'Phone can contain only digits, + and parentheses',
            latinOnly: 'Please use Latin characters only',
        },
    },
    pages: {
        mail: {
            title: 'Mail',
            forwardToHomeAddress: 'Configure mail forwarding to my home address',
            forwardToHomeAddressDescription:
                'All letters will be automatically forwarded to the specified address',
            configureForwarding: 'Configure forwarding',
            incomingMailSettings: 'Mail scanning settings',
            openQrCode: 'QR code key to Office Hof',
            orderForwarding: 'Order mail forwarding',
            forwarding: 'Paper mail forwarding',
            orderForwardingTitle: 'Order mail forwarding',
            orderOneTimeForwarding: 'Order one-time forwarding',
            orderForwardingMessage:
                'Order mail forwarding and we will send all your letters from Office Hof to your address',
            orderButton: 'Order',
            orderForwardingSuccess: 'Forwarding ordered',
            orderForwardingError: 'Error ordering forwarding',
            forwardingSentMessage: 'Letters have been forwarded to your address. Confirm receipt.',
            forwardingSentHint: 'If letters do not arrive within 10 days, contact support',
            forwardingTrackNumber: 'Tracking number',
            forwardingTrackOnBpost: 'Track on bpost',
            forwardingReceivedButton: 'Received forwarding',
            forwardingReceivedSuccess: 'Forwarding receipt confirmed',
            forwardingReceivedError: 'Error confirming receipt',
            forwardingEmptyMessage: 'Forwarding cancelled as the mailbox was empty at the time of request',
            singleForwardingStatus: {
                processing: 'Processing',
                completed: 'Completed',
                pending: 'Pending',
                cancelled: 'Cancelled',
                sent: 'Sent',
            },
            singleForwardingRequestDate: 'Request date',
            singleForwardingStatusLabel: 'Status',
            cancelButton: 'Cancel',
            qrCodeTitle: 'Personal QR Code',
            qrCodeDescription:
                'With this QR code you can get access to the office, booked meeting rooms and your mailbox',
            qrCodeDescriptionWithAddress:
                'With this QR code you can get access to the office at {address}, booked meeting rooms and your mailbox',
            scanContent: 'Scan letter content',
            scanContentDescription: 'Automatically scan incoming letter content',
            scanningSettings: 'Scanning Settings',
            scanningSettingsPaperStorageTooltip:
                'The original paper letters will be stored in your personal mailbox located within the office center.',
            scanningDescription:
                'If, for confidentiality reasons, you do not wish your physical mail to be opened and scanned by our staff, please disable the function below. Notifications about incoming mail will still be provided free of charge.',
            forwardingSettings: 'Forwarding Settings',
            regularForwarding: 'Regular Forwarding',
            oneTimeForwarding: 'One-time Forwarding',
            forwardMail: 'Forward mail',
            forwardNever: 'Never',
            forwardOncePerMonth: 'Once per month',
            forwardTwicePerMonth: 'Twice per month',
            forwardingDescription: 'Incoming mail forwarding will be to your home address: {address}',
            changeAddressHint: 'To change the address, write to support',
            changeAddressButton: 'Change address',
            forwardingInfo: 'You can set up regular forwarding or order a one-time forwarding',
            sendRegisteredMail: 'Send as registered mail',
            settingsSaved: 'Settings saved',
            saveError: 'Error saving settings',
            mailsList: 'Mail list',
            noMails: 'No mails',
            loading: 'Loading...',
            loadError: 'Error loading mails',
            unavailable: 'Unavailable',
            mailTypeLetter: 'Letter',
            mailTypeLetterNoScan: 'Letter (not scanned)',
            mailTypeParcel: 'Parcel',
            parcelTypeSmall: 'Small',
            letterNoScanModalTitle: 'Letter not scanned',
            letterNoScanModalMessage:
                'Your paper mail is stored at Office Hof. You can pick it up yourself or order forwarding by mail.',
            letterNoScanOrderButton: 'Order forwarding',
            parcelTypeBig: 'Big',
            viewButton: 'View',
            downloadButton: 'Download',
            paymentHintButton: 'Hint',
            paymentRequiredTitle: 'Payment Required',
            paymentRequiredMessage: 'You need to pay for a new period to view this mail',
            payButton: 'Pay',
            searchByDate: 'Search by date',
            thisWeek: 'This week',
            thisMonth: 'This month',
            parcelReceivedTitle: 'You received a {type} parcel',
            parcelReceivedBig: 'big',
            parcelReceivedSmall: 'small',
            parcelPickupAddress: 'You can pick it up at the office at the address',
            okButton: 'OK',
            dateFrom: 'From',
            dateTo: 'To',
            resetButton: 'Reset',
            applyButton: 'Apply',
            dateFromAfterTo: 'Date "From" cannot be later than "To"',
            dateToBeforeFrom: 'Date "To" cannot be earlier than "From"',
            clearFilter: 'Clear filter',
        },
        office: {
            title: 'Office Center',
            inDevelopment: 'In development',
            meetingRooms: 'Meeting Rooms',
            meetingRoomsDescription: 'Book and manage meeting rooms',
            coworking: 'Coworking',
            coworkingDescription: 'Book a coworking space',
            noCoworkingAvailable: 'No coworking spaces available',
            capacity: 'Capacity',
            people: 'people',
            fromPrice: 'From',
            perHour: 'per hour',
            includingVat: 'incl. VAT',
            exclVat: 'excl. VAT',
            book: 'Reserve',
            location: 'Location',
            selectLocation: 'Select location',
            allLocations: 'All locations',
            noRoomsAvailable: 'No rooms available',
            rooms: {
                charlemagne: {
                    name: '"Charlemagne" (Green)',
                    description:
                        'Equipment: Media projector, white board and markers, audio system, conferencing phone. Additional services: Coffee, tea and water are suggested for free during the reservation time.',
                },
                ambiorix: {
                    name: '"Ambiorix" (Red)',
                    description:
                        'Equipped with the LED projector & display, whiteboard and markers, audio system, conference phone. Additional services: Coffee, tea and water are suggested for free during the reservation time.',
                },
            },
            myBookings: 'My Bookings',
            noBookings: 'You have no bookings yet',
            bookingQrModalTitle: 'Meeting Room Access',
            bookingQrModalMessage: 'To access the meeting room, present the QR code',
            showQrCode: 'Show QR Code',
            hours: 'h',
            minutes: 'min',
            datePlaceholder: 'dd.mm.yyyy',
            duration: 'Duration',
            cost: 'Cost',
            insufficientFundsTitle: 'Insufficient Funds',
            insufficientFundsMessage: 'You have insufficient funds',
            insufficientFundsMessageWithAmount:
                'You have insufficient funds for booking. Your balance is missing €{amount}',
            showMore: 'More',
            hide: 'Hide',
            booking: 'Booking',
            selectDate: 'Select date',
            noSlotsAvailable: 'No slots available',
            roomNotSelected: 'Meeting room not selected',
            available: 'Available',
            booked: 'Booked',
            bookingSuccess: 'Successfully booked the meeting room',
            bookingError: 'Error creating booking',
            startTime: 'Start',
            endTime: 'End',
            selectStartTime: 'Select time',
            selectEndTime: 'Select time',
        },
        payment: {
            title: 'Payment',
            showContract: 'Contract with Office Hof',
            balance: 'Balance',
            extraChargesThisMonth: 'Additional services charged this month',
            extraChargesSectionTitle: 'Charges this month for additional services',
            extraServiceTypes: {
                scan: 'Mail scanning',
                parcels: 'Letter/parcel forwarding',
                bookings: 'Meeting room bookings',
                tariff: 'Tariff',
                postal_fees: 'Postal fees',
            },
            extraChargesTotalLabel: 'Total',
            tariff: 'Tariff',
            nextChargeDate: 'Next charge: ',
            tariffNotActivated: 'Tariff activates after payment',
            replenishBalance: 'Top up balance',
            replenish: 'Add funds',
            pay: 'Pay',
            paySubscriptionTitle: 'Pay for subscription',
            paySubscriptionMessage: 'This page will be available after you pay for your subscription',
            availableAfterPrepayment: 'Available after prepayment',
            enterAmount: 'Enter the amount and select the payment type',
            paymentCredited:
                'Usually payments are credited immediately, but in some cases it may take up to 2 days',
            paymentScreenTitle: 'Payment',
            enterAmountForPayment: 'Enter amount for payment',
            amountForPayment: 'Amount for payment',
            replenishBalanceOn: 'Replenish balance by:',
            totalToPay: 'Total to pay:',
            payByRequisites: 'Pay by requisites',
            requisiteCompany: 'Company',
            payOnline: 'Pay online',
            payFor1Month: 'Pay for 1 month',
            payFor3Months: 'Pay for 3 months',
            payFor6Months: 'Pay for 6 months',
            payFor12Months: 'Pay for 1 year',
            discount: 'Discount',
            discountOnTariff: 'Discount {discount}% on tariff for {months} months!',
            discountOnTariffTooltip:
                'When you pay €{amount}, you will get a {discount}% discount on the tariff for {months} months!',
            customAmount: 'Custom amount',
            minAmountError: 'Minimum payment amount: {amount}€',
            serverError: 'Server error, we are already looking into it',
            chargesHistory: 'Charges history',
            invoice: 'Invoice from',
            noInvoices: 'No invoices found',
            noTariff: 'You have no tariff',
            currentTariff: 'Current',
            sameAsPreviousTariff: 'Everything from {tariffName}, and also:',
            fromPrice: 'From',
            perMonth: 'per month',
            withoutVat: 'w/o VAT 21%',
            goToTariff: 'Switch plan',
            tariffUpgradeSubject: 'Request from customer {customer_id} to upgrade tariff to {tariff_name}',
            tariffUpgradeBody: 'Please upgrade my tariff to {tariff_name}',
            tariffFeatures: {
                legalAddress: 'The legal address for your company',
                receptionMails: 'Reception of your incoming mails (registered incl.) and packets',
                coffeeSoftDrinks: 'Coffee and soft drinks for you and your clients in the meeting room',
                access24_7: 'Access to your information and messages 24/7',
                sharedFaxLine: 'Shared incoming fax line (treated as the regular mail)',
                mailScanning:
                    'Your incoming mail scanning and redirection to your mailbox on the daily basis',
                freePages:
                    'Number of free-of-charge sheets (scanning and redirection to your mailbox A4 2-side) included in the month pass',
                personalBox: 'Personal box for document storage, access 24/7',
                ipTelephony: 'IP telephony service including extension in "Office Hof" IP PBX',
                freeMeetingHours: 'Number of free-of-charge reservation hours of the meeting rooms per month',
                freeParcels: 'Number of free-of-charge incoming parcels included in the month pass',
                discountLevel:
                    'Discount level at meeting room and working place reservation, mail scanning and parcel treatment',
                brusselsPhoneLine:
                    'Brussels direct phone line (also available different area and countries line numbers) via IP PBX',
                phoneCallsManagement:
                    'Incoming and outgoing phone calls and faxes management with the dedicated IP PBX',
            },
        },
        support: {
            title: 'Contact Support',
            phone: 'Phone',
            email: 'Email',
            phoneHint: '9 AM–6 PM, weekdays',
            writeButton: 'Write to Support',
            emailSubject: 'Request from customer {customer_id}',
            editDataEmailSubject: 'Data change request from customer {customer_id}',
            innEmailSubject: 'Information from customer {customer_id}',
            innEmailBody: 'Hello!\n\nMy Tax ID: \n',
            innModalTitle: 'Tax ID Required',
            innModalMessage: 'Please provide your Tax ID — we will send it for verification.',
            innModalSubmitButton: 'Submit',
            innModalLaterButton: 'Later',
            innModalCloseButton: 'Close',
            innRequiredModalTitle: 'Tax ID Required',
            innRequiredModalMessage:
                'The {n} days allowed for providing your Tax ID have passed. Enter it below to continue using the service.',
            innInputLabel: 'Tax ID',
            innInputPlaceholder: 'Enter your Tax ID',
            innInputRequired: 'Please enter your Tax ID',
            innResubmitButton: 'Send again',
            innPendingTitle: 'Tax ID under review',
            innPendingMessage:
                'We have received your Tax ID {tax_id} and sent it for verification. Access to the service stays limited until the check is complete.',
            innPendingResubmitHint: 'The check is taking longer than usual. You can send the Tax ID again.',
            innSubmitError: 'Failed to send the Tax ID. Please try again later.',
            innButton: 'Submit Tax ID',
            loading: 'Loading...',
            copied: 'Copied to clipboard',
        },
        recovery: {
            title: 'Recovery',
            description: 'To recover access to the account {email}, click the button below.',
            descriptionWithCompany:
                'To recover access to the account {email} for company {company_name}, click the button below.',
            button: 'Recover',
            successMessage: 'We have sent you an email with a recovery link.',
            successHint: 'If the email has not arrived, please try again later.',
            invalidEmail: 'Invalid email',
        },
        profile: {
            title: 'Profile',
            companyData: 'Company Data',
            contactPerson: 'Contact Person',
            mailingAddress: 'Mailing Address',
            settings: 'Settings',
            companyName: 'Name',
            inn: 'Tax ID',
            officeAddress: 'Office Address',
            name: 'Name',
            email: 'Email',
            phone: 'Phone',
            interfaceLanguage: 'Interface Language',
            editData: 'Edit Data',
            editDataMessage: 'To change your data or company data, please contact support',
            logoutButton: 'Logout',
            loading: 'Loading profile data...',
            languageNames: {
                ru: 'Russian',
                en: 'English',
                fr: 'French',
                nl: 'Dutch',
                de: 'German',
            },
        },
    },
    installPrompt: {
        title: 'Install the app',
        description: 'Install the app for faster and easier access to Office Hof services',
        install: 'Install',
        skip: 'Skip',
        androidTitle: 'Install on Android',
        androidSteps: [
            'Open the browser menu (three dots at the top right)',
            'Select «Add to Home screen» or «Install app»',
            'Confirm installation',
        ],
        iosTitle: 'Install on iPhone',
        iosSteps: [
            'Tap the Share button (square with arrow up) in Safari',
            'Scroll and select «Add to Home Screen»',
            'Tap «Add»',
        ],
        close: 'Close',
    },
};
